import { CommonModule } from '@angular/common';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Component, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/user/auth.service';
import { finalize, Subscription } from 'rxjs';
import { ToastComponent } from '../../shared/toast/toast.component';
import { TwoFactorAuthStatus } from '../../enums/two-factor-auth.enum';
import { GoogleQrCodeDetailsModel } from '../../core/models/user/google-qr-code-details.model';
import { AuthDetailsModel } from '../../core/models/user/auth-details.model';
import { passwordMatchValidator } from '../../validators/password-match-validator';
import { LanguageService } from '../../core/services/language.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LanguagesListModel } from '../../core/models/user/languages-model';
import { DashboardCategoriesModel } from '../../core/models/dashboard-categories.model';
import { environment } from '../../../environments/environment';
import { CustomersService } from '../../core/services/customer/customers.service';
import { UserSessionModel } from '../../core/models/user/current-user.model';
import { CurrentUserService } from '../../core/services/user/current-user.service';
import { CookieService } from '../../core/services/cookie.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastComponent,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  firstLoginForm: FormGroup;
  mailCodeForm: FormGroup;
  mailLoginForm: FormGroup;
  googleLoginForm: FormGroup;
  googleQrCodeForm: FormGroup;
  categories: DashboardCategoriesModel[] = [];
  currentUser: UserSessionModel | null = null;

  secretQuestions: any = [
    {
      id: 1,
      secretQuestion: this.translate.instant(
        'secretQuestions.favouriteRestaurant'
      ),
    },
    {
      id: 2,
      secretQuestion: this.translate.instant('secretQuestions.firstPet'),
    },
    {
      id: 3,
      secretQuestion: this.translate.instant(
        'secretQuestions.historicalPerson'
      ),
    },
    {
      id: 4,
      secretQuestion: this.translate.instant('secretQuestions.firstBoss'),
    },
    {
      id: 5,
      secretQuestion: this.translate.instant('secretQuestions.favouriteAuthor'),
    },
  ];

  langChangeSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private languageService: LanguageService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private cookieService: CookieService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);

    this.firstLoginForm = this.fb.group(
      {
        secretQuestion: [null, [Validators.required]],
        answer: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[A-Z])(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|])[A-Za-z\d!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|]{8,}$/
            ),
          ],
        ],
        confirmPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[A-Z])(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|])[A-Za-z\d!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|]{8,}$/
            ),
          ],
        ],
      },
      { validators: passwordMatchValidator }
    );
    this.mailCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleQrCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });

    this.categories = [
      {
        name: 'osasAnalysis',
        code: 'OSAS',
        image:
          'https://cdn.builder.io/api/v1/image/assets/TEMP/9c8742a9e840b4cbe2fcb20463cde2d0861c6b15f437671dc882d63538fd6b86?placeholderIfAbsent=true&apiKey=83d381f3414f4eb7aab2ad418c3f45ba',
        bgColor: 'bg-peach',
        link: `${environment.apiLocations.osas}`,
      },
      {
        name: 'dermatology',
        code: 'DERM',
        image:
          'https://cdn.builder.io/api/v1/image/assets/TEMP/131981b6192ca52501da56a6806e8dc4758584186c40487c870f261bbf8db0c7?placeholderIfAbsent=true&apiKey=83d381f3414f4eb7aab2ad418c3f45ba',
        bgColor: 'bg-light-blue',
        link: `${environment.apiLocations.derm}`,
      },
      {
        name: 'cardiology',
        code: 'CARDIOLOGY',
        image:
          'https://cdn.builder.io/api/v1/image/assets/TEMP/a045ba0be7e8348a2bb551c1fa1a1b957a4659bc70cb1f5b982133462c20a73d?placeholderIfAbsent=true&apiKey=83d381f3414f4eb7aab2ad418c3f45ba',
        bgColor: 'bg-light-yellow',
        link: 'http://localhost:4203/',
      },
      {
        name: 'diabetesTracking',
        code: 'DIAB',
        image:
          'https://cdn.builder.io/api/v1/image/assets/TEMP/fda94c266e1e7dc0229f14cedf2e12d65befb94d7d9964501d7b730a438869be?placeholderIfAbsent=true&apiKey=83d381f3414f4eb7aab2ad418c3f45ba',
        bgColor: 'bg-light-pink',
        link: `${environment.apiLocations.diab}`,
      },
      {
        name: 'settings',
        code: 'BACKOFFICE',
        image:
          'https://cdn.builder.io/api/v1/image/assets/TEMP/9ce6dee2ebe1d5b992adb28bab5f0f0a35b4962167539c9f8cc7fd99403f29fc?placeholderIfAbsent=true&apiKey=83d381f3414f4eb7aab2ad418c3f45ba',
        bgColor: 'bg-light-green',
        link: `${environment.apiLocations.backOffice}`,
      },
    ];
  }
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  loginForm = {
    email: '',
    password: '',
  };
  languages: LanguagesListModel[] = [
    {
      code: 'en',
      name: 'English',
      flag: 'assets/media/flags/united-states.svg',
    },
    {
      code: 'nl',
      name: 'Dutch',
      flag: 'assets/media/flags/netherlands.svg',
    },
  ];
  showToast: boolean = false;
  toastMessage: string = '';
  toastType: 'success' | 'error' | 'warning' = 'success';
  currentYear: number = new Date().getFullYear();
  loginLoading: boolean = false;
  firstLoginLoading: boolean = false;
  mailAuthSectionLoading: boolean = false;
  loginWithMailLoading: boolean = false;
  loginWithGoogleLoading: boolean = false;
  showFirstLogin: boolean = false;
  show2FA: boolean = false;
  showMailSection: boolean = false;
  showLoginWithMail: boolean = false;
  showLoginWithGoogle: boolean = false;
  googleQrCodeDetails: GoogleQrCodeDetailsModel = {
    qrCodeImageUrl: '',
    manuelEntrySetupCode: '',
  };
  loginPasswordVisible: boolean = false;
  currentLanguageName: string = '';
  currentLanguageFlag: string = '';

  ngOnInit() {
    const langCode = this.languageService.getLanguage();
    const selectedLang =
      this.languages.find((lang) => lang.code === langCode) ||
      this.languages[0];
    this.updateCurrentLanguage(selectedLang);
    this.loadTranslations();
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  loadTranslations(): void {
    this.translate
      .get([
        'secretQuestions.favouriteRestaurant',
        'secretQuestions.firstPet',
        'secretQuestions.historicalPerson',
        'secretQuestions.firstBoss',
        'secretQuestions.favouriteAuthor',
      ])
      .subscribe((translations) => {
        this.secretQuestions = [
          {
            id: 1,
            secretQuestion: translations['secretQuestions.favouriteRestaurant'],
          },
          { id: 2, secretQuestion: translations['secretQuestions.firstPet'] },
          {
            id: 3,
            secretQuestion: translations['secretQuestions.historicalPerson'],
          },
          { id: 4, secretQuestion: translations['secretQuestions.firstBoss'] },
          {
            id: 5,
            secretQuestion: translations['secretQuestions.favouriteAuthor'],
          },
        ];
      });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  setLanguage(lang: { code: string; name: string; flag: string }) {
    this.translate.use(lang.code);
    localStorage.setItem('selectedLanguage', lang.code);
    location.reload();
  }

  private updateCurrentLanguage(lang: {
    code: string;
    name: string;
    flag: string;
  }) {
    this.currentLanguageName = lang?.name;
    this.currentLanguageFlag = lang?.flag;
  }

  isFirstLoginControlInvalid(controlName: string): boolean {
    const control = this.firstLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isMailLoginControlInvalid(controlName: string): boolean {
    const control = this.mailLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isGoogleLoginControlInvalid(controlName: string): boolean {
    const control = this.googleLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onSecretQuestionChange(e: Event) {
    const selectedSecretQuestion = (e.target as HTMLSelectElement).value;
    this.firstLoginForm.get('secretQuestion')?.setValue(selectedSecretQuestion);
  }

  async submitLogin(e: Event): Promise<void> {
    e.preventDefault();
    this.loginLoading = true;
    const selectedLanguageCode = this.languageService.getLanguage();
    localStorage.setItem('isLogin', 'false');

    const res: AuthDetailsModel = await this.authService
      .login(
        this.loginForm.email,
        this.loginForm.password,
        selectedLanguageCode
      )
      .pipe(finalize(() => (this.loginLoading = false)))
      .toPromise();
    if (res?.status === 1) {
      this.showFirstLogin = true;
    } else {
      this.currentUser = this.currentUserService.GetSession();
      if (res?.authenticatorType === TwoFactorAuthStatus.None) {
        var existSingleApp = await this.getApplicationsByCustomerCode();
        if (!existSingleApp) {
          this.goToDashboard();
        }
      } else if (res?.authenticatorType === TwoFactorAuthStatus.Email) {
        this.showLoginWithMail = true;
      } else if (res?.authenticatorType === TwoFactorAuthStatus.Google) {
        this.showLoginWithGoogle = true;
      }
    }
  }

  submitFirstLogin(e: Event): void {
    e.preventDefault();
    if (this.firstLoginForm.valid) {
      this.firstLoginLoading = true;
      const formData = {
        email: this.loginForm.email,
        oldPassword: this.loginForm.password,
        ...this.firstLoginForm.value,
      };

      this.authService
        .firstLogin(formData)
        .pipe(
          finalize(() => {
            this.firstLoginLoading = false;
          })
        )
        .subscribe({
          next: async () => {
            this.showToast = false;
            this.show2FA = true;
            setTimeout(() => {
              this.displayToast(
                true,
                'Saving process completed successfully',
                'success'
              );
            }, 0);
          },
        });
    } else {
      this.firstLoginForm.markAllAsTouched();
      this.showToast = false;
      setTimeout(() => {
        this.displayToast(
          true,
          this.translate.instant(
            'alert.allFormFieldsRequiredAndPasswordMatched'
          ),
          'warning'
        );
      }, 0);
    }
  }

  async getApplicationsByCustomerCode(): Promise<boolean> {
    const res = await this.customersService
      .getApplicationsByCustomerCode(this.currentUser?.customerCode ?? '')
      .toPromise();
    this.cookieService.setCookie('applications', JSON.stringify(res), 1);
    const isLogin = localStorage.getItem('isLogin');
    if (res.length === 1 && isLogin === 'false') {
      localStorage.setItem('isLogin', 'true');
      // osas
      if (this.categories[0].code === res[0].applicationCode) {
        window.location.href = environment.apiLocations.osas;
        return true;
      }
      // derm
      else if (this.categories[1].code === res[0].applicationCode) {
        window.location.href = environment.apiLocations.derm;
        return true;
      }
      // diabet
      else if (this.categories[3].code === res[0].applicationCode) {
        window.location.href = environment.apiLocations.diab;
        return true;
      }
      // backoffice
      else if (this.categories[4].code === res[0].applicationCode) {
        window.location.href = environment.apiLocations.backOffice;
        return true;
      }
    }
    return false;
  }

  onGoogleAuthToggle(): void {
    this.authService.getGoogleAuth().subscribe({
      next: async (res: GoogleQrCodeDetailsModel) => {
        this.googleQrCodeDetails.qrCodeImageUrl = res?.qrCodeImageUrl;
        this.googleQrCodeDetails.manuelEntrySetupCode =
          res?.manuelEntrySetupCode;
      },
    });
  }

  onMailAuthToggle(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    if (isChecked) {
      this.showMailSection = true;
      this.authService.sendEmailAuthForEnable().subscribe({
        next: async () => {
          this.showMailSection = true;
        },
      });
    }
  }

  // Login with Mail
  async submitMailLoginForm(e: Event): Promise<void> {
    e.preventDefault();
    if (this.mailLoginForm.valid) {
      this.loginWithMailLoading = true;
      const formData = {
        email: this.loginForm.email,
        password: this.loginForm.password,
        ...this.mailLoginForm.value,
      };

      this.authService
        .loginWithMailAuthenticator(formData)
        .pipe(
          finalize(() => {
            this.loginWithMailLoading = false;
          })
        )
        .subscribe({
          next: async () => {
            this.showToast = false;
            setTimeout(() => {
              this.displayToast(
                true,
                'Process completed successfully',
                'success'
              );
            }, 0);

            const existSingleApp = await this.getApplicationsByCustomerCode();
            if (!existSingleApp) {
              this.goToDashboard();
            }
          },
        });
    } else {
      this.showToast = false;
      setTimeout(() => {
        this.displayToast(true, 'Code required', 'warning');
      }, 0);
      this.mailLoginForm.markAllAsTouched();
    }
  }

  // Login with Google
  submitGoogleLoginForm(e: Event): void {
    e.preventDefault();
    if (this.googleLoginForm.valid) {
      this.loginWithGoogleLoading = true;
      const formData = {
        email: this.loginForm.email,
        password: this.loginForm.password,
        ...this.googleLoginForm.value,
      };

      this.authService
        .loginWithGoogleAuthenticator(formData)
        .pipe(
          finalize(() => {
            this.loginWithGoogleLoading = false;
          })
        )
        .subscribe({
          next: async () => {
            this.showToast = false;
            setTimeout(() => {
              this.displayToast(
                true,
                'Process completed successfully',
                'success'
              );
            }, 0);
            const existSingleApp = await this.getApplicationsByCustomerCode();
            if (!existSingleApp) {
              this.goToDashboard();
            }
          },
        });
    } else {
      this.showToast = false;
      setTimeout(() => {
        this.displayToast(true, 'Code required', 'warning');
      }, 0);
      this.googleLoginForm.markAllAsTouched();
    }
  }

  submitMailAuthCode(e: Event): void {
    e.preventDefault();
    if (this.mailCodeForm.valid) {
      this.mailAuthSectionLoading = true;
      const formData = {
        ...this.mailCodeForm.value,
      };

      this.authService
        .enableEmailAuth(formData)
        // .pipe(
        //   finalize(() => {
        //     this.mailAuthSectionLoading = false;
        //   })
        // )
        .subscribe({
          next: async () => {
            this.showToast = false;
            setTimeout(() => {
              this.displayToast(
                true,
                'Process completed successfully',
                'success'
              );
            }, 0);
            setTimeout(() => {
               this.goToDashboard();
              this.signOut();
            }, 2000);
          },
        });
    } else {
      this.showToast = false;
      setTimeout(() => {
        this.displayToast(true, 'Code required', 'warning');
      }, 0);
      this.mailCodeForm.markAllAsTouched();
    }
  }

  submitGoogleQrCodeForm(e: Event): void {
    e.preventDefault();
    setTimeout(() => {
      if (this.googleQrCodeForm.valid) {
        const formData = {
          ...this.googleQrCodeForm.value,
        };

        this.authService.enableGoogleAuth(formData).subscribe({
          next: async () => {
            this.showToast = false;
            setTimeout(() => {
              this.displayToast(
                true,
                'Process completed successfully',
                'success'
              );
            }, 0);
            setTimeout(() => {
               this.goToDashboard();
              this.signOut();
            }, 2000);
          },
        });
      } else {
        this.showToast = false;
        setTimeout(() => {
          this.displayToast(true, 'Code required', 'warning');
        }, 0);
        this.googleQrCodeForm.markAllAsTouched();
      }
    }, 500);
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);

    // if (typeof window !== 'undefined') {
    //   window.location.href = `${environment.apiLocations.derm}`;
    // }
  }

  togglePasswordVisibility(type: string): void {
    if (type === 'login')
      this.loginPasswordVisible = !this.loginPasswordVisible;
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      this.cookieService.deleteCookie('applications');
    });
  }

  displayToast(show: boolean, message: string, type: string | any): void {
    this.showToast = show;
    this.toastMessage = message;
    this.toastType = type;
  }
}
